import { useState } from "react";
import {
  FolderIcon,
  PlusIcon,
  MoreHorizontalIcon,
  Loader2Icon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { CreateVaultDialog } from "./CreateVaultDialog";
import { RenameVaultDialog } from "./RenameVaultDialog";
import { DeleteVaultDialog } from "./DeleteVaultDialog";
import { toast } from "sonner";
import { format } from "date-fns";
import { TodoVault } from "@/lib/types";
import { useGetTodoVaults } from "@/hooks/queries/useGetTodoVaults";
import { useCreateTodoVault } from "@/hooks/queries/useCreateTodoVault";
import { useUpdateTodoVault } from "@/hooks/queries/useUpdateTodoVault";
import { useDeleteTodoVault } from "@/hooks/queries/useDeleteTodoVault";

export const InternalTodo = () => {
  const vaults = useGetTodoVaults();
  const createVault = useCreateTodoVault();
  const updateVault = useUpdateTodoVault();
  const deleteVault = useDeleteTodoVault();

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedVault, setSelectedVault] = useState<TodoVault | null>(null);

  const handleCreateVault = async (name: string) => {
    await createVault.mutate({ data: { name } });
    setCreateDialogOpen(false);
  };

  const handleRenameVault = async (name: string) => {
    if (!selectedVault) return;

    await updateVault.mutate({
      vaultId: selectedVault.id,
      data: { name },
    });
    setRenameDialogOpen(false);
    setSelectedVault(null);
  };

  const handleDeleteVault = async () => {
    if (!selectedVault) return;

    if (vaults.data?.length === 1) {
      toast.error("You cannot delete the last vault");
      return;
    }

    await deleteVault.mutate({ vaultId: selectedVault.id });
    setDeleteDialogOpen(false);
    setSelectedVault(null);
  };

  const openRenameDialog = (vault: TodoVault) => {
    setSelectedVault(vault);
    setRenameDialogOpen(true);
  };

  const openDeleteDialog = (vault: TodoVault) => {
    setSelectedVault(vault);
    setDeleteDialogOpen(true);
  };

  if (vaults.isError) {
    return (
      <Alert variant="destructive">
        <AlertDescription>
          Failed to load todo vaults. Please try again.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Internal Todo Vaults</h2>
        <Button
          onClick={() => setCreateDialogOpen(true)}
          size="sm"
          disabled={createVault.isPending}
        >
          {createVault.isPending ? (
            <Loader2Icon className="h-4 w-4 animate-spin" />
          ) : (
            <PlusIcon className="h-4 w-4" />
          )}
          Create Vault
        </Button>
      </div>

      <ScrollArea className="h-[400px]">
        <div className="flex flex-col gap-2">
          {vaults.isLoading ? (
            Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-20 w-full rounded-lg" />
            ))
          ) : vaults.data && vaults.data.length > 0 ? (
            vaults.data.map(vault => (
              <div
                key={vault.id}
                className="relative border-border pb-2 [&:not(:last-child)]:border-b"
              >
                <div className="flex items-center justify-between">
                  <h4 className="flex items-center gap-2 text-base">
                    <FolderIcon className="h-4 w-4" />
                    {vault.name}
                  </h4>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                        <MoreHorizontalIcon className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem
                        onClick={() => openRenameDialog(vault)}
                        className="cursor-pointer"
                      >
                        Rename
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={() => openDeleteDialog(vault)}
                        className="cursor-pointer text-destructive hover:bg-destructive/10 focus:text-destructive"
                      >
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
                <p className="text-sm text-muted-foreground">
                  Created {format(new Date(vault.createdAt), "MMM d, yyyy")}
                </p>
              </div>
            ))
          ) : (
            <div className="flex flex-col items-center justify-center py-8">
              <p className="text-muted-foreground">No vaults found</p>
              <Button
                onClick={() => setCreateDialogOpen(true)}
                variant="outline"
                size="sm"
                className="mt-2"
              >
                <PlusIcon className="mr-2 h-4 w-4" />
                Create your first vault
              </Button>
            </div>
          )}
        </div>
      </ScrollArea>

      <CreateVaultDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        onCreateVault={handleCreateVault}
      />

      <RenameVaultDialog
        open={renameDialogOpen}
        onOpenChange={setRenameDialogOpen}
        vault={selectedVault}
        onRenameVault={handleRenameVault}
      />

      <DeleteVaultDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        vault={selectedVault}
        onDeleteVault={handleDeleteVault}
      />
    </div>
  );
};
